/**
 * Emissions Display Formatter
 *
 * Utilities for formatting emissions values based on user display settings.
 * Handles unit conversion (kg/tonnes/short tons) and decimal places.
 */

import type { EmissionsDisplayUnit, EmissionSettings } from '../types/emissions';

// Valid range for decimalPlaces (toLocaleString supports 0-20)
const MIN_DECIMAL_PLACES = 0;
const MAX_DECIMAL_PLACES = 20;

// Conversion factors
const KG_PER_TONNE = 1000;
const KG_PER_SHORT_TON = 907.185; // 1 US short ton = 907.185 kg

// Unit display labels
export const UNIT_LABELS: Record<EmissionsDisplayUnit, string> = {
  kgCO2e: 'kgCO₂e',
  tCO2e: 'tCO₂e',
  short_ton: 'short tons CO₂e',
};

// Compact unit labels for small spaces
const UNIT_LABELS_COMPACT: Record<EmissionsDisplayUnit, string> = {
  kgCO2e: 'kg',
  tCO2e: 't',
  short_ton: 'st',
};

export interface DisplaySettings {
  displayUnit: EmissionsDisplayUnit;
  decimalPlaces: number;
}

export interface FormattedEmission {
  /** The converted numeric value */
  value: number;
  /** Formatted string value with decimal places applied */
  formatted: string;
  /** Full unit label (e.g., "tCO₂e") */
  unit: string;
  /** Compact unit label (e.g., "t") */
  unitCompact: string;
  /** Combined formatted value and unit (e.g., "1,234.5 tCO₂e") */
  display: string;
}

/**
 * Default display settings when none are configured
 */
export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  displayUnit: 'tCO2e',
  decimalPlaces: 2,
};

/**
 * Converts emission value from kg to the target display unit
 */
export const convertEmissionValue = (
  valueInKg: number,
  targetUnit: EmissionsDisplayUnit
): number => {
  switch (targetUnit) {
    case 'kgCO2e':
      return valueInKg;
    case 'tCO2e':
      return valueInKg / KG_PER_TONNE;
    case 'short_ton':
      return valueInKg / KG_PER_SHORT_TON;
    default: {
      // Log unexpected unit type - indicates API/frontend mismatch
      console.warn(`[EmissionsFormatter] Unknown display unit "${targetUnit}", defaulting to tonnes`);
      return valueInKg / KG_PER_TONNE;
    }
  }
};

/**
 * Validates and clamps decimalPlaces to a safe range for toLocaleString
 */
const clampDecimalPlaces = (decimalPlaces: number): number => {
  const safeValue = Math.max(MIN_DECIMAL_PLACES, Math.min(MAX_DECIMAL_PLACES, Math.floor(decimalPlaces)));
  if (safeValue !== decimalPlaces) {
    console.warn(`[EmissionsFormatter] Invalid decimalPlaces ${decimalPlaces}, using ${safeValue}`);
  }
  return safeValue;
};

/**
 * Formats a number with locale-aware thousands separators
 */
const formatWithSeparators = (value: number, decimalPlaces: number): string => {
  const safeDecimalPlaces = clampDecimalPlaces(decimalPlaces);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: safeDecimalPlaces,
    maximumFractionDigits: safeDecimalPlaces,
  });
};

/**
 * Creates a fallback FormattedEmission for invalid input values
 */
const createInvalidValueResult = (settings: DisplaySettings): FormattedEmission => {
  const unit = UNIT_LABELS[settings.displayUnit] || UNIT_LABELS.tCO2e;
  const unitCompact = UNIT_LABELS_COMPACT[settings.displayUnit] || UNIT_LABELS_COMPACT.tCO2e;
  return {
    value: 0,
    formatted: '--',
    unit,
    unitCompact,
    display: `-- ${unit}`,
  };
};

/**
 * Validates emission value input
 * Returns true if value is valid for formatting
 */
const isValidEmissionValue = (value: number): boolean => {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
};

/** Values not yet loaded or omitted by the API — show placeholder, no dev warning */
const isMissingEmissionValue = (valueInKg: unknown): valueInKg is null | undefined => {
  return valueInKg == null;
};

/**
 * Formats an emission value (assumed to be in kg) according to display settings
 */
export const formatEmission = (
  valueInKg: number | null | undefined,
  settings: DisplaySettings = DEFAULT_DISPLAY_SETTINGS
): FormattedEmission => {
  if (isMissingEmissionValue(valueInKg)) {
    return createInvalidValueResult(settings);
  }
  if (!isValidEmissionValue(valueInKg)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[EmissionsFormatter] Invalid emission value: ${valueInKg}`);
    }
    return createInvalidValueResult(settings);
  }

  const { displayUnit, decimalPlaces } = settings;
  const convertedValue = convertEmissionValue(valueInKg, displayUnit);
  const formatted = formatWithSeparators(convertedValue, decimalPlaces);
  const unit = UNIT_LABELS[displayUnit];
  const unitCompact = UNIT_LABELS_COMPACT[displayUnit];

  return {
    value: convertedValue,
    formatted,
    unit,
    unitCompact,
    display: `${formatted} ${unit}`,
  };
};

/**
 * Formats an emission value with automatic scaling for large numbers
 * Returns values like "1.2 MtCO₂e" or "456 ktCO₂e" for better readability
 */
export const formatEmissionCompact = (
  valueInKg: number | null | undefined,
  settings: DisplaySettings = DEFAULT_DISPLAY_SETTINGS
): FormattedEmission => {
  if (isMissingEmissionValue(valueInKg)) {
    return createInvalidValueResult(settings);
  }
  if (!isValidEmissionValue(valueInKg)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[EmissionsFormatter] Invalid emission value: ${valueInKg}`);
    }
    return createInvalidValueResult(settings);
  }

  const { displayUnit, decimalPlaces } = settings;
  let convertedValue = convertEmissionValue(valueInKg, displayUnit);
  let unit = UNIT_LABELS[displayUnit];
  let unitCompact = UNIT_LABELS_COMPACT[displayUnit];

  // Apply scaling for large values (only for kg and tonnes)
  if (displayUnit === 'kgCO2e' || displayUnit === 'tCO2e') {
    if (convertedValue >= 1000000) {
      convertedValue = convertedValue / 1000000;
      unit = displayUnit === 'kgCO2e' ? 'GtCO₂e' : 'MtCO₂e';
      unitCompact = displayUnit === 'kgCO2e' ? 'Gt' : 'Mt';
    } else if (convertedValue >= 1000) {
      convertedValue = convertedValue / 1000;
      unit = displayUnit === 'kgCO2e' ? 'MtCO₂e' : 'ktCO₂e';
      unitCompact = displayUnit === 'kgCO2e' ? 'Mt' : 'kt';
    }
  }

  const formatted = formatWithSeparators(convertedValue, Math.min(decimalPlaces, 1));

  return {
    value: convertedValue,
    formatted,
    unit,
    unitCompact,
    display: `${formatted} ${unit}`,
  };
};

/**
 * Extracts display settings from full EmissionSettings object
 */
export const getDisplaySettingsFromEmissionSettings = (
  settings?: EmissionSettings | null
): DisplaySettings => {
  if (!settings) {
    return DEFAULT_DISPLAY_SETTINGS;
  }
  return {
    displayUnit: settings.displayUnit || DEFAULT_DISPLAY_SETTINGS.displayUnit,
    decimalPlaces: settings.decimalPlaces ?? DEFAULT_DISPLAY_SETTINGS.decimalPlaces,
  };
};

/**
 * Creates a formatter function bound to specific display settings
 * Useful for memoizing in components
 */
export const createEmissionsFormatter = (settings: DisplaySettings = DEFAULT_DISPLAY_SETTINGS) => {
  return {
    format: (valueInKg: number | null | undefined) => formatEmission(valueInKg, settings),
    formatCompact: (valueInKg: number | null | undefined) => formatEmissionCompact(valueInKg, settings),
    settings,
  };
};
